import Input from "../../../components/input";
import Button from "../../../components/button";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  useEffect,
  useState,
} from "react";
import { apiEndPoints } from "../../../hooks/remote/apiEndPoints";
import { toast } from "react-toastify";

const VerifyResetOtp = () => {
  document.title =
    "Verify Reset OTP - CloudVantage";

  const navigate = useNavigate();
  const location = useLocation();

  const email =
    location.state?.email || "";

  const [loading, setLoading] =
    useState(false);

  const [
    resendLoading,
    setResendLoading,
  ] = useState(false);

  /*
   * Prevent direct access to this page
   * without first requesting a reset OTP.
   */
  useEffect(() => {
    if (!email) {
      navigate(
        "/password-reset-link",
        {
          replace: true,
        }
      );
    }
  }, [email, navigate]);

  const verifyOtpForm = useFormik({
    initialValues: {
      otp: "",
    },

    validationSchema: Yup.object({
      otp: Yup.string()
        .required(
          "Verification code is required"
        ),
    }),

    onSubmit: async (values) => {
      if (!email) return;

      try {
        setLoading(true);

        const response =
          await apiEndPoints.verifyResetOtp(
            {
              email,
              otp: values.otp,
            }
          );

        const data =
          response?.data?.data ||
          response?.data ||
          response;

        if (
          data?.statuscode &&
          data.statuscode !== "00"
        ) {
          toast.error(
            data?.statusmessage ||
              data?.message ||
              "Invalid or expired verification code."
          );

          return;
        }

        toast.success(
          data?.statusmessage ||
            "Verification successful."
        );

        navigate(
          "/reset-password",
          {
            state: {
              email,
              otp: values.otp,
            },
          }
        );
      } catch (error) {
        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.message ||
            "Unable to verify verification code."
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const handleResendOtp =
    async () => {
      if (!email) {
        toast.error(
          "Unable to identify your email address."
        );

        navigate(
          "/password-reset-link"
        );

        return;
      }

      try {
        setResendLoading(true);

        const response =
          await apiEndPoints.passwordResetLink(
            {
              email,
            }
          );

        const data =
          response?.data?.data ||
          response?.data ||
          response;

        if (
          data?.statuscode &&
          data.statuscode !== "00"
        ) {
          toast.error(
            data?.statusmessage ||
              data?.message ||
              "Unable to resend verification code."
          );

          return;
        }

        toast.success(
          data?.statusmessage ||
            "A new verification code has been sent."
        );

        verifyOtpForm.setFieldValue(
          "otp",
          ""
        );
      } catch (error) {
        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.message ||
            "Unable to resend verification code."
        );
      } finally {
        setResendLoading(false);
      }
    };

  return (
    <div className="px-4 flex justify-center items-center py-8">
      <div className="my-3 w-full md:w-[50%] lg:w-[40%]">
        <form
          onSubmit={
            verifyOtpForm.handleSubmit
          }
        >
          <div className="bg-white dark:bg-[#141D26] border border-[#c4c4c432] dark:text-white rounded-md px-6 py-10 grid gap-6">

            <div>
              <p className="pb-1 crimson-font text-lg font-semibold">
                Verify reset code
              </p>

              <p className="text-xs text-[#c4c4c4] pb-2 border-b dark:border-b-[#c4c4c432]">
                Enter the verification
                code sent to your email.
                The code expires after
                10 minutes.
              </p>
            </div>

            <div>
              <p className="text-xs text-[#8d8d8d] mb-1">
                Verification code sent to
              </p>

              <p className="text-sm font-medium break-all">
                {email}
              </p>
            </div>

            <Input
              label="Verification code"
              type="text"
              name="otp"
              placeholder="Enter verification code"
              value={
                verifyOtpForm.values.otp
              }
              onBlur={
                verifyOtpForm.handleBlur
              }
              onChange={(e) => {
                const value =
                  e.target.value.replace(
                    /\D/g,
                    ""
                  );

                verifyOtpForm.setFieldValue(
                  "otp",
                  value
                );
              }}
              onError={
                verifyOtpForm.touched.otp &&
                verifyOtpForm.errors.otp
              }
            />

            <div>
              <Button
                buttonRole="submit"
                loading={loading}
                buttonText="Verify code"
              />
            </div>

            <div className="text-sm">
              <span className="text-[#8d8d8d]">
                Didn't receive the
                code?{" "}
              </span>

              <button
                type="button"
                onClick={
                  handleResendOtp
                }
                disabled={
                  resendLoading
                }
                className="text-[#1d9bf0] font-medium disabled:opacity-50"
              >
                {resendLoading
                  ? "Sending..."
                  : "Resend code"}
              </button>
            </div>

          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyResetOtp;